import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCompass,
  FiCreditCard,
  FiFileText,
  FiMap,
  FiMessageCircle,
  FiShield,
  FiTag,
  FiUser,
} from 'react-icons/fi';
import './landing-page.css';
import PromptIdeas from './PromptIdeas';
import { trackEvent, trackLandingPageView } from '../../services/analytics';
import { savePlanningDraft } from '../../services/planningDraft';
import { MAX_USER_QUERY_LENGTH } from '../../constants/planning';

const HERO_PROMPT = 'Plan a 5-day Lisbon trip for two with boutique stays, great food, and one day trip.';
const QUICK_STARTS = [
  { label: '5 days in Lisbon', icon: '☀️', prompt: HERO_PROMPT },
  { label: 'Weekend in Tuscany', icon: '🍇', prompt: 'Plan a 3-day weekend in Tuscany for two with hill towns, local food, and a relaxed pace. Compare options with and without a car.' },
  { label: '10 days in Japan', icon: '⛩️', prompt: 'Plan a 10-day first trip to Japan for two, splitting time between Tokyo and Kyoto. Include local food, cultural sights, and realistic travel days.' },
  { label: 'Swiss Alps', icon: '🏔️', prompt: 'Plan a 5-day Swiss Alps trip for two with scenic trains and gentle hikes. Help compare bases and costs, and flag seasonal limitations.' },
];

const EXAMPLE_DAYS = [
  {
    day: 'Day 1',
    title: 'Arrive, settle in, eat well',
    details: 'Land, check in, explore Alfama, catch sunset at Miradouro de Santa Luzia, and have dinner in a local taverna.',
    Icon: FiCalendar,
  },
  {
    day: 'Day 2',
    title: 'Food, viewpoints, neighborhoods',
    details: 'Visit Mercado da Ribeira, São Jorge Castle, Graça viewpoints, Chiado, and Príncipe Real without backtracking.',
    Icon: FiCompass,
  },
  {
    day: 'Day 3',
    title: 'Sintra without the rush',
    details: 'Take the morning train to Sintra, pace the main sights realistically, and return to Lisbon before sunset.',
    Icon: FiMap,
  },
];

const REFINEMENTS = [
  'Make it more affordable',
  'Add a romantic evening',
  'Swap the day trip',
];

const WORKFLOW = [
  ['Tell us the trip', 'Destination, dates, budget, vibe, and travelers.'],
  ['Get a practical plan', 'Stays, food, activities, logistics, and day-by-day flow.'],
  ['Refine with context', 'Ask for cheaper, calmer, romantic, or family-friendly changes.'],
  ['Book confidently', 'Compare trusted partner options and verify before purchase.'],
];

const COMPARISON_ROWS = [
  {
    name: 'Generic AI chat',
    description: 'Ideas you still have to organize',
    Icon: FiMessageCircle,
  },
  {
    name: 'OdysseyAI',
    description: 'A practical plan you can refine and save',
    Icon: FiCompass,
    featured: true,
  },
  {
    name: 'Manual planning',
    description: 'Blogs, maps, reviews, and spreadsheets',
    Icon: FiFileText,
  },
];

const TRUST_ITEMS = [
  ['No automatic booking', FiShield],
  ['Verify prices before purchase', FiTag],
  ['Guest mode works free', FiUser],
  ['One-time planning credits', FiCreditCard],
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [tripRequest, setTripRequest] = useState('');
  const [showMobileCta, setShowMobileCta] = useState(false);
  const heroSubmitRef = useRef(null);
  const normalizedTripRequest = tripRequest.trim();
  const tripRequestLength = normalizedTripRequest.length;
  const tripRequestOverLimit = tripRequestLength > MAX_USER_QUERY_LENGTH;
  const tripRequestNearLimit = !tripRequestOverLimit && tripRequestLength >= MAX_USER_QUERY_LENGTH * 0.9;

  useEffect(() => {
    trackLandingPageView({ entry_point: 'homepage' });
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !heroSubmitRef.current) return;
    // Never cover the hero's submit button with a duplicate mobile action.
    const observer = new IntersectionObserver(([entry]) => {
      setShowMobileCta(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    });
    observer.observe(heroSubmitRef.current);
    return () => observer.disconnect();
  }, []);

  const beginPlanning = (prompt, label) => {
    trackEvent('landing_cta_clicked', { label });
    if (prompt) savePlanningDraft(prompt);
    navigate('/chat', {
      state: {
        ...(prompt ? { prefillPrompt: prompt } : {}),
        entryPoint: label,
      },
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (tripRequestOverLimit) {
      document.getElementById('landing-trip-request')?.focus();
      return;
    }
    beginPlanning(
      normalizedTripRequest || null,
      normalizedTripRequest ? 'hero_prompt_submit' : 'hero_empty_submit',
    );
  };

  return (
    <div className="oa-landing">
      <section className="oa-hero" aria-labelledby="landing-title">
        <div className="oa-shell oa-hero__grid">
          <div className="oa-hero__content">
            <p className="oa-hero__eyebrow">Grounded trip planning</p>
            <h1 id="landing-title">Plan the trip. Know what to book next.</h1>
            <p className="oa-hero__lede">
              Share your destination, dates, budget, and vibe. Get a practical, source-backed
              itinerary—with booking options only when partner inventory is grounded.
            </p>

            <form className="oa-planner" onSubmit={handleSubmit} aria-label="Start planning a trip">
              <label htmlFor="landing-trip-request" className="visually-hidden">
                Describe the trip you want to plan
              </label>
              <div className="oa-planner__field">
                <FiCompass aria-hidden />
                <textarea
                  id="landing-trip-request"
                  value={tripRequest}
                  onChange={(event) => setTripRequest(event.target.value)}
                  placeholder="Where do you want to go?"
                  rows="2"
                  aria-describedby="landing-trip-request-feedback"
                  aria-invalid={tripRequestOverLimit || undefined}
                />
              </div>
              <p
                id="landing-trip-request-feedback"
                className={`oa-planner__feedback${tripRequestOverLimit ? ' oa-planner__feedback--error' : tripRequestNearLimit ? ' oa-planner__feedback--warning' : ''}`}
                role={tripRequestOverLimit ? 'alert' : undefined}
              >
                {tripRequestOverLimit
                  ? `Please shorten your request by ${(tripRequestLength - MAX_USER_QUERY_LENGTH).toLocaleString()} characters.`
                  : `${tripRequestLength.toLocaleString()} / ${MAX_USER_QUERY_LENGTH.toLocaleString()} characters${tripRequestNearLimit ? ' · Almost at the limit' : ''}`}
              </p>
              <div className="oa-quick-starts" role="group" aria-label="Try a trip idea">
                {QUICK_STARTS.map((idea) => <button
                  key={idea.label}
                  type="button"
                  className="oa-quick-start"
                  onClick={() => {
                    setTripRequest(idea.prompt);
                    trackEvent('prompt_idea_clicked', { category: 'hero_quick_start', label: idea.label });
                    document.getElementById('landing-trip-request')?.focus({ preventScroll: true });
                  }}
                >
                  <span aria-hidden>{idea.icon}</span> {idea.label}
                </button>)}
              </div>
              <div className="oa-planner__actions">
                <button
                  ref={heroSubmitRef}
                  type="submit"
                  className="oa-button oa-button--primary"
                  disabled={tripRequestOverLimit}
                >
                  Plan my trip <FiArrowRight aria-hidden />
                </button>
                <a href="#example-plan" className="oa-text-link">
                  See an example plan <FiArrowRight aria-hidden />
                </a>
              </div>
              <p className="oa-planner__trust">
                <FiCheck aria-hidden /> Free guest planning · No automatic booking · Affiliate links are labeled
              </p>
            </form>
          </div>

          <div className="oa-hero__visual" aria-label="Example Lisbon itinerary preview">
            <img
              src="/assets/travel/lisbon-rooftops.jpg"
              alt="Lisbon rooftops overlooking the Tagus River"
              width="1536"
              height="1024"
              loading="eager"
              fetchpriority="high"
            />
            <div className="oa-itinerary-preview">
              <p className="oa-itinerary-preview__title">Lisbon · 5 days</p>
              <div className="oa-mini-day oa-mini-day--active">
                <span><FiCalendar aria-hidden /></span>
                <div><strong>Day 1</strong><p>Arrive, settle in, eat well</p></div>
              </div>
              <div className="oa-mini-day">
                <span><FiCompass aria-hidden /></span>
                <div><strong>Day 2</strong><p>Food, viewpoints, neighborhoods</p></div>
              </div>
              <div className="oa-itinerary-preview__skeleton" aria-hidden>
                <span /><span />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="example-plan" className="oa-proof" aria-labelledby="proof-title">
        <div className="oa-shell">
          <h2 id="proof-title">A plan you can use</h2>
          <div className="oa-proof__grid">
            <div className="oa-itinerary-card">
              <div className="oa-itinerary-card__header">
                <div>
                  <p>Example itinerary</p>
                  <h3>Lisbon · 5 days</h3>
                </div>
                <span>Moderate budget</span>
              </div>
              <div className="oa-itinerary-timeline">
                {EXAMPLE_DAYS.map(({ day, title, details, Icon }, index) => (
                  <article className={index === 0 ? 'is-active' : ''} key={day}>
                    <span className="oa-itinerary-timeline__icon"><Icon aria-hidden /></span>
                    <div>
                      <h4><strong>{day}</strong> · {title}</h4>
                      <p>{details}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="oa-proof__aside">
              <p className="oa-proof__description">
                OdysseyAI turns a rough request into a realistic route with neighborhoods, timing,
                food, stays, and next booking steps.
              </p>
              <div className="oa-refinement-list" aria-label="Example itinerary refinements">
                {REFINEMENTS.map((refinement) => (
                  <button
                    type="button"
                    key={refinement}
                    onClick={() => beginPlanning(`${HERO_PROMPT} ${refinement}.`, 'example_refinement')}
                  >
                    <span>{refinement}</span><FiArrowRight aria-hidden />
                  </button>
                ))}
              </div>
            </div>

            <figure className="oa-proof__photo">
              <img
                src="/assets/travel/lisbon-tram.jpg"
                alt="A yellow tram passing tiled buildings in Lisbon"
                width="1003"
                height="1568"
                loading="lazy"
              />
            </figure>
          </div>
        </div>
      </section>

      <PromptIdeas />

      <section className="oa-workflow" aria-labelledby="workflow-title">
        <div className="oa-shell">
          <h2 id="workflow-title">From idea to itinerary,<br />without the tab overload.</h2>
          <ol className="oa-workflow__steps">
            {WORKFLOW.map(([title, description], index) => (
              <li key={title}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="oa-comparison" aria-labelledby="comparison-title">
        <div className="oa-shell">
          <div className="oa-comparison__grid">
            <div>
              <h2 id="comparison-title">More useful than generic chat. Faster than manual planning.</h2>
              <p>
                OdysseyAI turns your request into a structured, real-world plan—so you spend less
                time searching and more time looking forward.
              </p>
            </div>
            <div className="oa-comparison__rows">
              {COMPARISON_ROWS.map(({ name, description, Icon, featured }) => (
                <div className={featured ? 'oa-comparison-row is-featured' : 'oa-comparison-row'} key={name}>
                  <Icon aria-hidden />
                  <strong>{name}</strong>
                  <span>{description}</span>
                  <FiArrowRight aria-hidden />
                </div>
              ))}
            </div>
          </div>
          <div className="oa-trust-rail" aria-label="Planning and purchase safeguards">
            {TRUST_ITEMS.map(([label, Icon]) => (
              <span key={label}><Icon aria-hidden />{label}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="oa-closing" aria-labelledby="closing-title">
        <div className="oa-shell oa-closing__grid">
          <div>
            <h2 id="closing-title">Your next trip deserves a plan you can trust.</h2>
            <p>Start with a rough idea. Leave with a realistic itinerary you can refine, save, and use.</p>
            <button type="button" className="oa-button oa-button--primary" onClick={() => beginPlanning(null, 'closing_cta')}>
              Plan my trip <FiArrowRight aria-hidden />
            </button>
            <small>Free guest planning · No subscription required</small>
          </div>
          <img
            src="/assets/travel/coastal-train-clean-v2.jpg"
            alt="A coastal railway seen through a train window at golden hour"
            width="1536"
            height="1024"
            loading="lazy"
          />
        </div>
      </section>

      {showMobileCta && <div className="oa-mobile-cta">
        <button
          type="button"
          className="oa-button oa-button--primary"
          disabled={tripRequestOverLimit}
          onClick={() => beginPlanning(normalizedTripRequest || null, 'mobile_sticky_cta')}
        >
          Plan my trip <FiArrowRight aria-hidden />
        </button>
      </div>}
    </div>
  );
};

export default LandingPage;
